<template>
  <div class="bar-list">
    <div v-for="row in rows" :key="row.id ?? row.label" class="bar-list-row">
      <div class="bar-list-head">
        <span class="flex items-center bar-list-label">
          <span data-testid="ai-chat-bar-list-label">{{ row.label }}</span>
          <el-tooltip
            v-if="row.resolved === false"
            :content="t('aichat.main.blocks.bar_list.id_only_hint')"
            placement="top"
          >
            <el-tag
              size="small"
              type="info"
              effect="plain"
              data-testid="ai-chat-bar-list-id-only-tag"
            >
              {{ t('aichat.main.blocks.bar_list.id_only') }}
            </el-tag>
          </el-tooltip>
        </span>
        <span class="bar-list-value" data-testid="ai-chat-bar-list-value">
          {{ formatValue(row.value, block.unit) }}
        </span>
      </div>
      <template v-if="row.share !== null">
        <div class="bar-list-track" aria-hidden="true">
          <div
            class="bar-list-fill"
            data-testid="ai-chat-bar-list-fill"
            :style="{ width: `${fillWidth(row.share)}%` }"
          />
        </div>
        <span class="bar-list-share" data-testid="ai-chat-bar-list-share">
          <span aria-hidden="true">{{ row.share }}%</span>
          <span class="sr-only">
            {{
              t('aichat.main.blocks.bar_list.share', {
                label: row.label,
                share: row.share
              })
            }}
          </span>
        </span>
      </template>
    </div>
    <div class="bar-list-total" data-testid="ai-chat-bar-list-total">
      {{
        t('aichat.main.blocks.bar_list.total', {
          total: formatValue(block.total, block.unit)
        })
      }}
      <span v-if="block.hasMore" data-testid="ai-chat-bar-list-has-more">
        · {{ t('aichat.main.blocks.bar_list.has_more') }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { BarListBlock } from '../../types'
import { useFormatValue } from './format'

/* label · value · bar rows; the share of the total is printed, not left to the bar length alone */
const props = defineProps<{
  block: BarListBlock
}>()

const { t } = useI18n()
const formatValue = useFormatValue()

/* a missing or non-numeric share draws no bar and prints no percentage instead of 'NaN%' */
const rows = computed(() =>
  props.block.rows.map((row) => ({
    ...row,
    share:
      typeof row.share === 'number' && Number.isFinite(row.share)
        ? row.share
        : null
  }))
)

const fillWidth = (share: number): number => Math.max(0, Math.min(100, share))
</script>

<style scoped>
.bar-list-row {
  display: grid;
  grid-template-areas:
    'head head'
    'track share';
  grid-template-columns: 1fr 3.25rem;
  gap: 0.25rem 0.5rem;
  padding: 0.25rem 0;
  border-bottom: 1px solid var(--el-border-color-main);
}

.bar-list-head {
  display: flex;
  grid-area: head;
  justify-content: space-between;
  gap: 0.5rem;
  color: var(--el-text-color-primary);
  font-size: 0.75rem;
}

.bar-list-label {
  gap: 0.375rem;
}

.bar-list-value {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.bar-list-track {
  grid-area: track;
  align-self: center;
  height: 0.375rem;
  overflow: hidden;
  border-radius: 0.25rem;
  background: var(--el-fill-color);
}

.bar-list-fill {
  height: 100%;
  border-radius: 0.25rem;
  background: var(--primary-color);
}

.bar-list-share {
  grid-area: share;
  color: var(--el-text-color-secondary);
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.bar-list-total {
  margin-top: 0.5rem;
  color: var(--el-text-color-secondary);
  font-size: 0.75rem;
}
</style>
